import { useState } from 'react'
import {
  Typography, Breadcrumb, Table, Button, Modal, Input, InputNumber, Select, Space, Popconfirm,
} from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, DatabaseOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import {
  useBaseTariffs,
  useCreateBaseTariff,
  useUpdateBaseTariff,
  useDeleteBaseTariff,
  useSeedBaseTariffs,
} from '@/hooks/useBaseTariffs'
import { useAuthStore } from '@/store/auth'
import type { BaseTariff } from '@/api/refBaseTariffs'

const { Title } = Typography

const CARE_TYPE_OPTIONS = [
  { value: 'КС', label: 'КС (круглосуточный)' },
  { value: 'ДС', label: 'ДС (дневной)' },
]

const rowKey = (r: BaseTariff) => `${r.year}_${r.care_type}`

export default function BaseTariffsPage() {
  const user = useAuthStore((s) => s.user)
  const isSuperadmin = user?.role === 'superadmin'

  const { data, isLoading, refetch } = useBaseTariffs()
  const createMutation = useCreateBaseTariff()
  const updateMutation = useUpdateBaseTariff()
  const deleteMutation = useDeleteBaseTariff()
  const seedMutation = useSeedBaseTariffs()

  // Create modal
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [newYear, setNewYear] = useState<number>(2026)
  const [newCareType, setNewCareType] = useState<string>('КС')
  const [newTariff, setNewTariff] = useState<number | null>(null)
  const [newSource, setNewSource] = useState('')

  // Inline edit — keyed by year+care_type
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editingTariff, setEditingTariff] = useState<number | null>(null)
  const [editingSource, setEditingSource] = useState('')

  const items = data?.items || []

  const handleCreate = async () => {
    if (!newTariff) return
    await createMutation.mutateAsync({
      year: newYear,
      care_type: newCareType,
      base_tariff: newTariff,
      source: newSource || undefined,
    })
    setIsModalVisible(false)
    setNewTariff(null)
    setNewSource('')
    refetch()
  }

  const handleEdit = (record: BaseTariff) => {
    setEditingKey(rowKey(record))
    setEditingTariff(record.base_tariff)
    setEditingSource(record.source || '')
  }

  const handleSaveEdit = async (record: BaseTariff) => {
    await updateMutation.mutateAsync({
      year: record.year,
      careType: record.care_type,
      data: {
        base_tariff: editingTariff ?? undefined,
        source: editingSource || undefined,
      },
    })
    setEditingKey(null)
    refetch()
  }

  const handleCancelEdit = () => {
    setEditingKey(null)
  }

  const handleDelete = async (record: BaseTariff) => {
    await deleteMutation.mutateAsync({ year: record.year, careType: record.care_type })
    refetch()
  }

  const handleSeed = async () => {
    await seedMutation.mutateAsync()
    refetch()
  }

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)

  const columns: ColumnsType<BaseTariff> = [
    {
      title: 'Год',
      dataIndex: 'year',
      key: 'year',
      width: 90,
      sorter: (a, b) => a.year - b.year,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Тип помощи',
      dataIndex: 'care_type',
      key: 'care_type',
      width: 130,
      filters: [
        { text: 'КС (круглосуточный)', value: 'КС' },
        { text: 'ДС (дневной)', value: 'ДС' },
      ],
      onFilter: (value, record) => record.care_type === value,
      render: (ct: string) => ct === 'КС' ? 'КС (круглосут.)' : 'ДС (дневной)',
    },
    {
      title: 'Базовая ставка (руб.)',
      dataIndex: 'base_tariff',
      key: 'base_tariff',
      width: 180,
      align: 'right',
      render: (value: number, record: BaseTariff) => {
        if (editingKey === rowKey(record)) {
          return (
            <InputNumber
              value={editingTariff}
              onChange={(v) => setEditingTariff(v)}
              min={0}
              step={0.01}
              precision={2}
              style={{ width: 150 }}
              onPressEnter={() => handleSaveEdit(record)}
            />
          )
        }
        return <strong>{formatCurrency(value)}</strong>
      },
      sorter: (a, b) => a.base_tariff - b.base_tariff,
    },
    {
      title: 'Регион',
      dataIndex: 'region_code',
      key: 'region_code',
      width: 90,
      render: (code: string) => code === '50' ? '50 (МО)' : code,
    },
    {
      title: 'Источник',
      dataIndex: 'source',
      key: 'source',
      render: (text: string | null, record: BaseTariff) => {
        if (editingKey === rowKey(record)) {
          return (
            <Input
              value={editingSource}
              onChange={(e) => setEditingSource(e.target.value)}
              onPressEnter={() => handleSaveEdit(record)}
              placeholder="Источник"
            />
          )
        }
        return text || '-'
      },
    },
    ...(isSuperadmin ? [{
      title: 'Действия',
      key: 'actions',
      width: 120,
      render: (_: any, record: BaseTariff) => {
        if (editingKey === rowKey(record)) {
          return (
            <Space size="small">
              <Button type="link" size="small" onClick={() => handleSaveEdit(record)}>
                Сохранить
              </Button>
              <Button type="link" size="small" onClick={handleCancelEdit}>
                Отмена
              </Button>
            </Space>
          )
        }
        return (
          <Space size="small">
            <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" />
            <Popconfirm
              title="Удалить тариф?"
              description="Это действие нельзя отменить"
              onConfirm={() => handleDelete(record)}
              okText="Удалить"
              cancelText="Отмена"
            >
              <Button type="text" danger icon={<DeleteOutlined />} size="small" />
            </Popconfirm>
          </Space>
        )
      },
    }] : []) as ColumnsType<BaseTariff>,
  ]

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item>Администрирование</Breadcrumb.Item>
        <Breadcrumb.Item>Базовые тарифы</Breadcrumb.Item>
      </Breadcrumb>

      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2} style={{ margin: 0 }}>Базовые тарифы (ТС МО)</Title>
        {isSuperadmin && (
          <Space>
            {items.length === 0 && (
              <Button
                icon={<DatabaseOutlined />}
                onClick={handleSeed}
                loading={seedMutation.isPending}
              >
                Заполнить из ТС МО
              </Button>
            )}
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalVisible(true)}>
              Добавить тариф
            </Button>
          </Space>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={items}
        rowKey={rowKey}
        loading={isLoading}
        pagination={false}
        size="middle"
      />

      <Modal
        title="Добавить базовый тариф"
        open={isModalVisible}
        onOk={handleCreate}
        onCancel={() => setIsModalVisible(false)}
        okText="Создать"
        cancelText="Отмена"
        okButtonProps={{ disabled: !newTariff }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <label>Год:</label>
            <InputNumber
              style={{ width: '100%', marginTop: 4 }}
              value={newYear}
              onChange={(v) => setNewYear(v || 2026)}
              min={2020}
              max={2050}
            />
          </div>
          <div>
            <label>Тип помощи:</label>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              value={newCareType}
              onChange={setNewCareType}
              options={CARE_TYPE_OPTIONS}
            />
          </div>
          <div>
            <label>Базовая ставка (руб.):</label>
            <InputNumber
              style={{ width: '100%', marginTop: 4 }}
              value={newTariff}
              onChange={setNewTariff}
              min={0}
              step={0.01}
              precision={2}
              placeholder="Например, 36864.19"
            />
          </div>
          <div>
            <label>Источник:</label>
            <Input
              style={{ marginTop: 4 }}
              value={newSource}
              onChange={(e) => setNewSource(e.target.value)}
              placeholder="ТС МО (с учётом КД)"
            />
          </div>
        </Space>
      </Modal>
    </div>
  )
}
